import React, { useState, useEffect } from 'react';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import './UserManagement.css';

interface CognitoUser {
  username: string;
  userId: string;
  attributes?: {
    email?: string;
    name?: string;
    nickname?: string;
    sub?: string;
    [key: string]: string | undefined;
  };
}

const UserManagement: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<CognitoUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    setLoading(true);
    setError(null);
    try {
      // First get basic user info
      const user = await getCurrentUser();
      
      // Then try to get user attributes
      try {
        const attributes = await fetchUserAttributes();
        console.log('User attributes:', attributes);
        
        setCurrentUser({
          username: user.username,
          userId: user.userId,
          attributes: attributes
        });
      } catch (attrError) {
        console.error('Error fetching user attributes:', attrError);
        // Still set the user with basic info
        setCurrentUser({
          username: user.username,
          userId: user.userId
        });
      }
    } catch (error: any) {
      console.error('Error fetching current user:', error);
      setError('Error al cargar información del usuario: ' + (error.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (): string => {
    if (!currentUser) return 'Usuario Desconocido';
    
    // Try to get name from attributes in order of preference
    if (currentUser.attributes?.name) {
      return currentUser.attributes.name;
    }
    
    if (currentUser.attributes?.nickname) {
      return currentUser.attributes.nickname;
    }
    
    // Fall back to username
    return currentUser.username;
  };

  const getUserEmail = (): string => {
    if (!currentUser) return '';
    
    // Try to get email from attributes
    if (currentUser.attributes?.email) {
      return currentUser.attributes.email;
    }
    
    // Fall back to username if it looks like an email
    if (currentUser.username.includes('@')) {
      return currentUser.username;
    }
    
    return 'No email available';
  };

  if (loading) {
    return <div className="loading">Cargando información de usuario...</div>;
  }

  return (
    <div className="user-management-container">
      <h2>Usuarios del Sistema</h2>
      
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Cerrar</button>
        </div>
      )}

      <div className="current-user-section">
        <h3>Tu Usuario</h3>
        {currentUser ? (
          <div className="current-user-info">
            <div className="info-row">
              <div className="info-label">Nombre:</div>
              <div className="info-value">{getUserName()}</div>
            </div>
            <div className="info-row">
              <div className="info-label">Correo Electrónico:</div>
              <div className="info-value">{getUserEmail()}</div>
            </div>
            <div className="info-row">
              <div className="info-label">ID de Usuario:</div>
              <div className="info-value user-id">{currentUser.userId}</div>
            </div>
            <div className="info-row">
              <div className="info-label">Nombre de Usuario:</div>
              <div className="info-value">{currentUser.username}</div>
            </div>
            {currentUser.attributes?.sub && (
              <div className="info-row">
                <div className="info-label">Identificador único:</div>
                <div className="info-value user-id">{currentUser.attributes.sub}</div>
              </div>
            )}
          </div>
        ) : (
          <p className="no-user-info">No se pudo cargar la información del usuario actual.</p>
        )}
      </div>

      <div className="all-users-section">
        <h3>Todos los Usuarios</h3>
        <div className="info-message">
          <p>Para ver todos los usuarios en Almida, es necesario usar el panel de administración de AWS Cognito.</p>
          <p>Actualmente, hay dos usuarios configurados en el sistema: "User One" y "User Two".</p>
          <p>Para agregar, modificar o eliminar usuarios, se requiere acceso al panel de administración de AWS.</p>
        </div>
      </div>
      
      <div className="user-management-footer">
        <p>Para más información o asistencia, contacte al administrador del sistema.</p>
      </div>
    </div>
  );
};

export default UserManagement;